import Alert from "@material-ui/lab/Alert"
import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"
import Typography from "@material-ui/core/Typography"
import { UserViewModel } from "../api/user"
import { useUserMutations } from "../hooks/use-user-mutations"
import { useTranslation } from "react-i18next"
import React from "react"
import { useAppDispatch } from "../../store"
import { api } from "../../api"

type Translations = typeof defaultTranslations

const defaultTranslations = {
  hourReportFeatureTitle: "Hour Report Feature",
  hourReportFeatureDescription:
    "Enabling hour report feature allows you to track your working hours and generate reports.",
  hourReportFeatureEnabled: "Hour report feature is enabled.",
  hourReportFeatureDisabled: "Hour report feature is currently disabled. Click the button below to enable it.",
  enableHourReportButton: "Enable Hour Report Feature",
  disableHourReportButton: "Disable Hour Report Feature",
}

export const useTranslations = (): Translations => {
  const { t } = useTranslation("hour-report")
  return {
    hourReportFeatureTitle: t("feature.title", { defaultValue: defaultTranslations.hourReportFeatureTitle }),
    hourReportFeatureDescription: t("feature.description", {
      defaultValue: defaultTranslations.hourReportFeatureDescription,
    }),
    hourReportFeatureEnabled: t("feature.enabled", { defaultValue: defaultTranslations.hourReportFeatureEnabled }),
    hourReportFeatureDisabled: t("feature.disabled", { defaultValue: defaultTranslations.hourReportFeatureDisabled }),
    enableHourReportButton: t("feature.enable", { defaultValue: defaultTranslations.enableHourReportButton }),
    disableHourReportButton: t("feature.disable", { defaultValue: defaultTranslations.disableHourReportButton }),
  }
}

const HourReportFeature = ({ user }: { user: UserViewModel }) => {
  const { updateUserInfo } = useUserMutations()
  const translations = useTranslations()
  const dispatch = useAppDispatch()

  const alertSeverity = user.isHourReportingEnabled ? "success" : "info"
  const alertMessage = user.isHourReportingEnabled
    ? translations.hourReportFeatureEnabled
    : translations.hourReportFeatureDisabled
  const buttonText = user.isHourReportingEnabled
    ? translations.disableHourReportButton
    : translations.enableHourReportButton

  React.useEffect(() => {
    dispatch(api.util.invalidateTags(["NavItems"]))
  }, [user.isHourReportingEnabled, dispatch])

  return (
    <>
      <Typography variant="subtitle1" gutterBottom>
        {translations.hourReportFeatureTitle}
      </Typography>
      <Typography variant="body2" gutterBottom color="textSecondary">
        {translations.hourReportFeatureDescription}
      </Typography>

      <Box sx={{ mt: 2 }}>
        <Alert variant={"outlined"} severity={alertSeverity} style={{ marginBottom: 16 }}>
          {alertMessage}
        </Alert>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            updateUserInfo(user.id, { featureToggles: { enableHourReporting: !user.isHourReportingEnabled } })
          }}
        >
          {buttonText}
        </Button>
      </Box>
    </>
  )
}

export default HourReportFeature
